// components/Records/RecordDisplay/EditableFieldsTable.tsx

import React, { useState } from "react";
import { Typography, Fade, Box } from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import EditableField, {
  EditableFieldType,
  SelectOption,
} from "./EditableField";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

export type EditableFieldConfig<T> = {
  field: keyof T;
  label: string;
  multiline?: boolean;
  editable?: boolean;
  type?: EditableFieldType;
  placeholder?: string;
  min?: number;
  max?: number;
  step?: number;
  options?: Array<SelectOption | string>;
};

export interface EditableFieldsTableProps<T> {
  record: T | null;
  fields: EditableFieldConfig<T>[];
  onSave: (field: keyof T, value: any) => Promise<void> | void;
  title?: string;
  sx?: object;
}

const EditableFieldsTable = <T,>({
  record,
  fields,
  onSave,
  title,
  sx = {},
}: EditableFieldsTableProps<T>) => {
  const [showSuccess, setShowSuccess] = useState(false);

  if (!record) return null;

  const handleSave = async (field: keyof T, value: any) => {
    await onSave(field, value);

    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 2000);
  };

  return (
    <Box sx={{ position: "relative", ...sx }}>
      <Fade in={showSuccess}>
        <Box
          sx={{
            position: "absolute",
            top: -4,
            right: -4,
            zIndex: 2,
          }}
        >
          <CheckCircleIcon color="success" fontSize="medium" />
        </Box>
      </Fade>

      {title && (
        <Typography
          variant="h6"
          sx={{
            mb: 2,
            fontWeight: 600,
          }}
        >
          {title}
        </Typography>
      )}

      <Grid2
        container
        columnSpacing={{ xs: 0, sm: 2 }}
        rowSpacing={{ xs: 0.5, sm: 1 }}
      >
        {fields.map((cfg) => {
          const { field, label } = cfg;

          return (
            <React.Fragment key={String(field)}>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 500,
                    color: "text.secondary",
                    overflowWrap: "anywhere",

                    // On mobile, place the label close to its field.
                    mb: { xs: 0.25, sm: 0 },
                  }}
                >
                  {label}
                </Typography>
              </Grid2>

              <Grid2
                size={{ xs: 12, sm: 6 }}
                sx={{
                  // Add separation before the next label on mobile.
                  mb: { xs: 1.5, sm: 0 },
                  minWidth: 0,
                }}
              >
                <EditableField
                  record={record}
                  {...cfg}
                  onSave={handleSave}
                />
              </Grid2>
            </React.Fragment>
          );
        })}
      </Grid2>
    </Box>
  );
};

export default EditableFieldsTable;